import React, { useEffect, useState } from "react";
import {
  Autocomplete,
  Avatar,
  Box,
  Button,
  FormControl,
  FormControlLabel,
  Paper,
  Radio,
  RadioGroup,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import { connect } from "react-redux";
import {
  downloadEmployeeLeaveReport,
  getEmployeeLeaves,
  getLeaveMonthlyLeaveCounts,
  getLeavePlanProgressTracker,
  getLeaveRequestStatus,
  getOtherLeaveSummary,
  getPublicHolidays,
} from "../../../store/leave/actions";
import {
  DashboardBarChart,
  DashboardDoughnutChart,
  DashboardLineChart,
} from "../../admin/components/StructureDashboard";
import moment from "moment";
import { DesktopDatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import FilterAltOffRoundedIcon from "@mui/icons-material/FilterAltOffRounded";
import FilterAltRoundedIcon from "@mui/icons-material/FilterAltRounded";
import ShowChartRoundedIcon from "@mui/icons-material/ShowChartRounded";
import StackedBarChartRoundedIcon from "@mui/icons-material/StackedBarChartRounded";
import {
  getEmployeeGroups,
  getTreeUnits,
} from "../../../store/structure/actions";
import { isEmpty } from "lodash";
import ListAltRoundedIcon from "@mui/icons-material/ListAltRounded";
import EventCalendar from "../components/EventCalendar";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";

const initPlanProgressTracker = {
  approvedPlans: 0,
  fullApprovedPlans: 0,
  partialApprovedPlans: 0,
  pendingPlans: 0,
  rejectedPlans: 0,
  allEmployees: 0,
  notStartedPlanningEmployees: 0,
  startedPlanningEmployees: 0,
  finishPlanningEmployees: 0,
};

const initRequestStatus = {
  pendingPlan: 0,
  pendingOther: 0,
  totalPending: 0,
  approvedPlan: 0,
  approvedOther: 0,
  totalApproved: 0,
};

const initLoadingValue = {
  planProgressTracker: true,
  planRequestStatus: true,
  monthlyLeaveCounts: true,
  weeklyLeaveSummary: true,
  calendarEvents: true,
  otherLeaveSummary: true,
  publicHolidays: true,
  report: false,
};

const defaultFilterValue = {
  unit: {
    unitId: null,
    unitName: "All",
  },
  employeeGroup: {
    id: null,
    name: "All",
  },
  dateRange: {
    startDate: null,
    endDate: null,
  },
};

const OversightDashboard = (props) => {
  const {
    showFilter,
    showReport,
    isTechnicalHead = false,
    selectedFiscalYear,
    publicHolidays,
    getLeavePlanProgressTracker,
    getLeaveRequestStatus,
    units,
    getTreeUnits,
    user,
    employeeGroups,
    getEmployeeGroups,
    getLeaveMonthlyLeaveCounts,
    getEmployeeLeaves,
    downloadEmployeeLeaveReport,
    getOtherLeaveSummary,
    getPublicHolidays,
    refresh,
    roleType = "HR",
  } = props;
  const [loadingData, setLoadingData] = useState(initLoadingValue);
  const [planProgressTracker, setPlanProgressTracker] = useState(
    initPlanProgressTracker
  );
  const [planRequestStatus, setPlanRequestStatus] = useState(initRequestStatus);
  const [selectedFilters, setSelectedFilters] = useState(defaultFilterValue);
  const [sortedUnits, setSortedUnits] = useState([]);
  const [searchFilter, setSearchFilter] = useState(false);
  const [monthlyStatistics, setMonthlyStatistics] = useState(null);
  const [monthlyStatisticOptions, setMonthlyStatisticOptions] = useState({
    chartType: "bar",
    isHorizontal: false,
    isStacked: true,
  });
  const [weeklyLeaveSummary, setWeeklyLeaveSummary] = useState([]);
  const [reportPeriod, setReportPeriod] = useState("");
  const [calendarEvents, setCalendarEvents] = useState([]);
  const [calendarDateRange, setCalendarDateRange] = useState({
    startDate: null,
    endDate: null,
    prev: null,
  });
  const [otherLeaveSummary, setOtherLeaveSummary] = useState([]);

  const handleLoadingData = (searchValue) => {
    const query = !!searchValue
      ? { ...defaultFilterValue, isTechnicalHead, type: roleType }
      : {
          unitId: selectedFilters.unit?.unitId || "",
          employeeGroupId: selectedFilters.employeeGroup?.id || "",
          startDate: selectedFilters.dateRange?.startDate || "",
          endDate: selectedFilters.dateRange?.endDate || "",
          type: roleType,
          isTechnicalHead,
        };

    getLeavePlanProgressTracker(query, (status, res) => {
      setLoadingData((prev) => ({ ...prev, planProgressTracker: status }));
      if (res) setPlanProgressTracker(res);
    });

    getLeaveRequestStatus(query, (status, res) => {
      setLoadingData((prev) => ({ ...prev, planRequestStatus: status }));
      if (res) setPlanRequestStatus(res);
    });

    getLeaveMonthlyLeaveCounts(query, (status, res) => {
      setLoadingData((prev) => ({ ...prev, monthlyLeaveCounts: status }));
      if (!!res) {
        setMonthlyStatistics(res);
      }
    });

    getEmployeeLeaves({ ...query, isWeekly: true }, (status, res) => {
      setLoadingData((prev) => ({ ...prev, weeklyLeaveSummary: status }));
      if (!!res) {
        setWeeklyLeaveSummary(res.events);
      }
    });

    getEmployeeLeaves(
      {
        ...query,
        isMonthly: true,
        calendarRange: JSON.stringify({ ...calendarDateRange }),
      },
      (status, res) => {
        setLoadingData((prev) => ({ ...prev, calendarEvents: status }));
        if (!!res) {
          setCalendarEvents(res.events);
          if (!calendarDateRange.prev) {
            setCalendarDateRange((pre) => ({ ...pre, ...res.calendarRange }));
          }
        }
      }
    );

    setSearchFilter(false);
  };

  useEffect(() => {
    handleLoadingData();

    if (publicHolidays.length === 0) {
      getPublicHolidays((status) =>
        setLoadingData((prev) => ({ ...prev, publicHolidays: status }))
      );
    } else setLoadingData((prev) => ({ ...prev, publicHolidays: false }));

    if (!otherLeaveSummary.length && roleType === "HUO") {
      getOtherLeaveSummary((status, data) => {
        setLoadingData((prev) => ({ ...prev, otherLeaveSummary: status }));
        if (data) {
          setOtherLeaveSummary(data);
        }
      });
    } else {
      setLoadingData((prev) => ({ ...prev, otherLeaveSummary: false }));
    }

    return () => {
      setLoadingData(initLoadingValue);
      setPlanProgressTracker(initPlanProgressTracker);
      setPlanRequestStatus(initRequestStatus);
    };
  }, []);

  useEffect(() => {
    handleLoadingData();

    getPublicHolidays((status) =>
      setLoadingData((prev) => ({ ...prev, publicHolidays: status }))
    );

    if (roleType === "HUO") {
      getOtherLeaveSummary((status, data) => {
        setLoadingData((prev) => ({ ...prev, otherLeaveSummary: status }));
        if (data) {
          setOtherLeaveSummary(data);
        }
      });
    }
  }, [selectedFiscalYear?.id, refresh]);

  useEffect(() => {
    if (!!showFilter && isEmpty(units) && roleType === "HR") {
      if (isEmpty(units)) getTreeUnits(user?.selectedEntitySector?.unitId);
    }
    if (!employeeGroups.length) getEmployeeGroups();
  }, [showFilter]);

  useEffect(() => {
    if (!!calendarDateRange.prev) {
      const query = {
        unitId: selectedFilters.unit?.unitId || "",
        employeeGroupId: selectedFilters.employeeGroup?.id || "",
        startDate: selectedFilters.dateRange?.startDate || "",
        endDate: selectedFilters.dateRange?.endDate || "",
        type: roleType,
        isTechnicalHead,
      };
      setCalendarEvents([]);
      getEmployeeLeaves(
        {
          ...query,
          isMonthly: true,
          calendarRange: JSON.stringify({ ...calendarDateRange }),
        },
        (status, res) => {
          setLoadingData((prev) => ({ ...prev, calendarEvents: status }));
          if (!!res) {
            setCalendarEvents(res.events);
          }
        }
      );
    }
  }, [calendarDateRange]);

  useEffect(() => {
    if (showFilter && !isEmpty(units)) {
      const sorted = units.sort((a, b) => {
        if (a.parentUnitId === null) return -1;
        if (b.parentUnitId === null) return 1;
        return 0;
      });
      setSortedUnits(sorted.filter((item) => !!item.parentUnitId));
    }
  }, [showFilter, units]);

  const PLANNING_STATUS_DOUGHNUT_CHART = {
    labels: ["Not Started", "Started Planning", "Finished Planning"],
    datasets: [
      {
        data: [
          planProgressTracker.notStartedPlanningEmployees,
          planProgressTracker.startedPlanningEmployees,
          planProgressTracker.finishPlanningEmployees,
        ],
        backgroundColor: [
          "rgba(255, 99, 132, 0.6)", // Soft Red for "Not Started"
          "rgba(255, 205, 86, 0.6)", // Soft Yellow for "Started Planning"
          "rgba(75, 192, 192, 0.6)", // Soft Teal for "Finished Planning"
        ],
        borderColor: [
          "rgba(255, 99, 132, 1)", // Red border for "Not Started"
          "rgba(255, 205, 86, 1)", // Yellow border for "Started Planning"
          "rgba(75, 192, 192, 1)", // Teal border for "Finished Planning"
        ],
        borderWidth: 1,
      },
    ],
  };

  const PLAN_PROGRESS_TRACKER_DOUGHNUT_CHART = {
    labels: [
      "Pending Plans",
      "Approved Plans",
      "Partially Approved Plans",
      "Fully Approved Plans",
      "Rejected Plans",
    ],
    datasets: [
      {
        data: [
          planProgressTracker.pendingPlans,
          planProgressTracker.approvedPlans,
          planProgressTracker.partialApprovedPlans,
          planProgressTracker.fullApprovedPlans,
          planProgressTracker.rejectedPlans,
        ],
        backgroundColor: [
          "rgba(153, 102, 255, 0.6)", // Purple for "Pending Plans"
          "rgba(75, 192, 75, 0.6)", // Green for "Approved Plans"
          "rgba(255, 206, 86, 0.6)", // Yellow for "Partially Approved Plans"
          "rgba(54, 162, 235, 0.6)", // Light Blue for "Fully Approved Plans"
          "rgba(255, 99, 132, 0.6)", // Red for "Rejected Plans"
        ],
        borderColor: [
          "rgba(153, 102, 255, 1)", // Purple border for "Pending Plans"
          "rgba(75, 192, 75, 1)", // Green border for "Approved Plans"
          "rgba(255, 206, 86, 1)", // Yellow border for "Partially Approved Plans"
          "rgba(54, 162, 235, 1)", // Blue border for "Fully Approved Plans"
          "rgba(255, 99, 132, 1)", // Red border for "Rejected Plans"
        ],
        borderWidth: 1,
      },
    ],
  };

  const PLAN_REQUEST_STATUS_DOUGHNUT_CHART = {
    labels: ["Pending", "Approved"],
    datasets: [
      {
        data: [
          planRequestStatus.pendingPlanRequest,
          planRequestStatus.approvedPlanRequest,
        ],
        backgroundColor: [
          "rgba(255, 159, 64, 0.6)", // Soft Orange for "Pending"
          "rgba(54, 162, 235, 0.6)", // Light Blue for "Approved"
        ],
        borderColor: [
          "rgba(255, 159, 64, 1)", // Orange border for "Pending"
          "rgba(54, 162, 235, 1)", // Blue border for "Approved"
        ],
        borderWidth: 1,
      },
    ],
  };

  const OTHER_REQUEST_STATUS_DOUGHNUT_CHART = {
    labels: ["Pending", "Approved"],
    datasets: [
      {
        data: [
          planRequestStatus.pendingOtherRequest,
          planRequestStatus.approvedOtherRequest,
        ],
        backgroundColor: [
          "rgba(255, 159, 64, 0.6)", // Soft Orange for "Pending"
          "rgba(54, 162, 235, 0.6)", // Light Blue for "Approved"
        ],
        borderColor: [
          "rgba(255, 159, 64, 1)", // Orange border for "Pending"
          "rgba(54, 162, 235, 1)", // Blue border for "Approved"
        ],
        borderWidth: 1,
      },
    ],
  };

  const EMPLOYEE_AVAILABILITY_STATUS_DOUGHNUT_CHART = {
    labels: ["On-Track", "Overdue"],
    datasets: [
      {
        data: [planRequestStatus.validLeave, planRequestStatus.overdueLeave],
        backgroundColor: [
          "rgba(75, 192, 75, 0.6)", // Green for "Approved Plans"
          "rgba(255, 99, 132, 0.6)", // Soft Red for "Not Started"
        ],
        borderColor: [
          "rgba(75, 192, 75, 1)", // Green border for "Approved Plans"
          "rgba(255, 99, 132, 1)", // Red border for "Not Started"
        ],
        borderWidth: 1,
      },
    ],
  };

  const APPROVED_LEAVE_STATUS_DOUGHNUT_CHART = {
    labels: ["Taken", "On-Leave", "Upcoming"],
    datasets: [
      {
        data: [
          planRequestStatus.takenLeave,
          planRequestStatus.onLeave,
          planRequestStatus.pendingApprovedRequest,
        ],
        backgroundColor: [
          "rgba(75, 192, 75, 0.6)", // Green for "Approved Plans"
          "rgba(75, 192, 192, 0.6)", // Soft Teal for "Finished Planning"
          "rgba(153, 102, 255, 0.6)", // Purple
        ],
        borderColor: [
          "rgba(75, 192, 75, 1)",
          "rgba(75, 192, 192, 1)",
          "rgba(153, 102, 255, 1)",
        ],
        borderWidth: 1,
      },
    ],
  };

  return (
    <>
      {(!!showFilter || !!showReport) && (
        <div className="border rounded dashboard-card elevated mt-2 mx-4">
          <div className="row justify-content-center pb-2 pt-3 px-3">
            {roleType === "HR" && (
              <div className="elevation-2 col-12 col-lg-2 col-md-3 col-sm-4">
                <Autocomplete
                  size="small"
                  id="selectedUnit"
                  defaultValue={null}
                  value={selectedFilters.unit || null}
                  options={[
                    {
                      unitId: null,
                      unitName: "All",
                    },
                    ...sortedUnits,
                  ]}
                  isOptionEqualToValue={(option, value) =>
                    option.unitId === value.unitId
                  }
                  onChange={(event, selectedUnit) => {
                    setSelectedFilters({
                      ...selectedFilters,
                      unit: selectedUnit || null,
                    });
                    setSearchFilter(true);
                  }}
                  getOptionLabel={(option) => option.unitName}
                  renderOption={(props, selectedUnit) => (
                    <Box component="li" {...props} key={selectedUnit.unitId}>
                      {selectedUnit.unitName}
                    </Box>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Unit"
                      placeholder="Unit"
                      inputProps={{
                        ...params.inputProps,
                        // autoComplete: "selectedUnit",
                      }}
                    />
                  )}
                />
              </div>
            )}
            <div className="col-12 col-lg-2 col-md-3 col-sm-4 mt-3 mt-sm-0">
              <Autocomplete
                size="small"
                id="selectedEmployeeGroup"
                defaultValue={null}
                value={selectedFilters.employeeGroup || null}
                options={[
                  {
                    id: null,
                    name: "All",
                  },
                  ...(employeeGroups || []),
                ]}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                onChange={(event, selectedGroup) => {
                  setSelectedFilters({
                    ...selectedFilters,
                    employeeGroup: selectedGroup || null,
                  });
                  setSearchFilter(true);
                }}
                getOptionLabel={(option) => option.name}
                renderOption={(props, selectedGroup) => (
                  <Box component="li" {...props}>
                    {selectedGroup.name}
                  </Box>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Employee Groups"
                    placeholder="Employee Groups"
                    inputProps={{
                      ...params.inputProps,
                    }}
                  />
                )}
              />
            </div>
            <div className="col-12 col-lg-3 col-md-3 col-sm-4 mt-2 mt-sm-0">
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <div className="d-flex align-items-center mt-n1">
                  <DesktopDatePicker
                    label="Leave Start Date"
                    inputFormat="dd/MM/yyyy"
                    disabled={!!showReport && reportPeriod !== ""}
                    value={selectedFilters.dateRange.startDate}
                    onChange={(date) => {
                      setSelectedFilters((prev) => ({
                        ...prev,
                        dateRange: {
                          startDate: date,
                          endDate: null,
                        },
                      }));
                      setSearchFilter(true);
                    }}
                    renderInput={(params) => (
                      <TextField
                        onKeyDown={(e) => e.preventDefault()}
                        required={true}
                        fullWidth
                        size="small"
                        {...params}
                      />
                    )}
                    // disablePast={!selectedPlan && !leaveToPostpone}
                  />

                  <span className="mx-2" style={{ fontSize: "30px" }}>
                    -
                  </span>

                  <DesktopDatePicker
                    label="Leave End Date"
                    inputFormat="dd/MM/yyyy"
                    disabled={!!showReport && reportPeriod !== ""}
                    value={selectedFilters.dateRange.endDate}
                    onChange={(date) => {
                      setSelectedFilters((prev) => ({
                        ...prev,
                        dateRange: {
                          startDate: prev.dateRange.startDate,
                          endDate: date,
                        },
                      }));
                      setSearchFilter(true);
                    }}
                    renderInput={(params) => (
                      <TextField
                        onKeyDown={(e) => e.preventDefault()}
                        required={true}
                        fullWidth
                        size="small"
                        {...params}
                      />
                    )}
                    // disablePast={!selectedPlan && !leaveToPostpone}
                  />
                </div>
              </LocalizationProvider>
            </div>
            {!!showReport && (
              <div className="col-12 d-flex align-items-center justify-content-center mt-2">
                <FormControl disabled={loadingData.report}>
                  <RadioGroup
                    row
                    aria-labelledby="report-filter-periods-label"
                    name="report-filter-periods"
                    value={reportPeriod}
                    onChange={(e) => setReportPeriod(e.target.value)}
                  >
                    <FormControlLabel
                      value="this-year"
                      control={<Radio />}
                      label="This Year"
                    />
                    <FormControlLabel
                      value="this-month"
                      control={<Radio />}
                      label="This Month"
                    />
                    <FormControlLabel
                      value="this-week"
                      control={<Radio />}
                      label="This Week"
                    />
                    <FormControlLabel
                      value="today"
                      control={<Radio />}
                      label="To Day"
                    />
                    <FormControlLabel
                      value=""
                      control={<Radio />}
                      label="Open"
                    />
                  </RadioGroup>
                </FormControl>
              </div>
            )}

            <div className="col-12 d-flex align-items-center justify-content-center mt-2">
              {!!showFilter && (
                <Button
                  variant="outlined"
                  size="small"
                  className="mr-3"
                  color="inherit"
                  startIcon={<FilterAltOffRoundedIcon />}
                  onClick={() => {
                    setSelectedFilters(defaultFilterValue);
                    handleLoadingData({
                      unitId: "",
                      employeeGroupId: "",
                      startDate: "",
                      endDate: "",
                    });
                  }}
                  disabled={
                    !selectedFilters.unit?.unitId &&
                    !selectedFilters.employeeGroup?.id &&
                    !selectedFilters.dateRange.startDate &&
                    !selectedFilters.dateRange.endDate
                  }
                >
                  Cancel
                </Button>
              )}
              {!!showFilter && (
                <Button
                  variant="contained"
                  size="small"
                  endIcon={<FilterAltRoundedIcon />}
                  disabled={!searchFilter}
                  onClick={() => handleLoadingData()}
                >
                  Search
                </Button>
              )}
              {!!showReport && (
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<DownloadRoundedIcon />}
                  disabled={loadingData.report}
                  onClick={() =>
                    downloadEmployeeLeaveReport(
                      {
                        unitId: selectedFilters.unit?.unitId || "",
                        employeeGroupId:
                          selectedFilters.employeeGroup?.id || "",
                        startDate: selectedFilters.dateRange?.startDate || "",
                        endDate: selectedFilters.dateRange?.endDate || "",
                        type: roleType,
                        isTechnicalHead,
                        reportPeriod,
                      },
                      (status) => {
                        setLoadingData((prev) => ({
                          ...prev,
                          report: status,
                        }));
                      }
                    )
                  }
                >
                  Generate
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="row w-100 mb-3 mt-1 px-4">
        <div className="col-12 col-md-9 my-md-0 my-2">
          {/* pie and doughnut charts */}
          <div className="row justify-content-center w-100 my-3 px-4">
            <div className="col-12 col-md-4 col-lg-3 my-md-0 my-2 pb-3">
              <DashboardDoughnutChart
                chartTitle="Annual Leave Planning Status"
                chartData={PLANNING_STATUS_DOUGHNUT_CHART}
                loading={loadingData.planProgressTracker}
              />
            </div>
            <div className="col-12 col-md-4 col-lg-3 my-md-0 my-2 pb-3">
              <DashboardDoughnutChart
                chartTitle="Leave Plan Progress"
                chartData={PLAN_PROGRESS_TRACKER_DOUGHNUT_CHART}
                loading={loadingData.planProgressTracker}
              />
            </div>
            <div className="col-12 col-md-4 col-lg-3 my-md-0 my-2 pb-3">
              <DashboardDoughnutChart
                chartTitle="On-Track vs. Overdue Leave Plans"
                chartData={EMPLOYEE_AVAILABILITY_STATUS_DOUGHNUT_CHART}
                loading={loadingData.planRequestStatus}
              />
            </div>
            <div className="col-12 col-md-4 col-lg-3 my-md-0 my-2 pb-3">
              <DashboardDoughnutChart
                chartTitle="Plan Leave Request Status"
                chartData={PLAN_REQUEST_STATUS_DOUGHNUT_CHART}
                loading={loadingData.planRequestStatus}
              />
            </div>
            <div className="col-12 col-md-4 col-lg-3 my-md-0 my-2 pb-3">
              <DashboardDoughnutChart
                chartTitle="Other Leave Request Status"
                chartData={OTHER_REQUEST_STATUS_DOUGHNUT_CHART}
                loading={loadingData.planRequestStatus}
              />
            </div>
            <div className="col-12 col-md-4 col-lg-3 my-md-0 my-2 pb-3">
              <DashboardDoughnutChart
                chartTitle="Approved Leave Requests Status"
                chartData={APPROVED_LEAVE_STATUS_DOUGHNUT_CHART}
                loading={loadingData.planRequestStatus}
              />
            </div>
          </div>

          <div className="border rounded p-2">
            <div className="d-flex align-items-center justify-content-end mb-2">
              <ToggleButtonGroup
                value={monthlyStatisticOptions.chartType}
                exclusive
                onChange={(event, newType) =>
                  setMonthlyStatisticOptions((prev) => ({
                    ...prev,
                    chartType: newType,
                  }))
                }
                size="small"
                color="primary"
                aria-label="chart type"
                disabled={loadingData.monthlyLeaveCounts}
              >
                <ToggleButton value="bar" aria-label="stacked bar chart">
                  <StackedBarChartRoundedIcon fontSize="inherit" />
                </ToggleButton>
                <ToggleButton value="line" aria-label="line chart">
                  <ShowChartRoundedIcon fontSize="inherit" />
                </ToggleButton>
                <ToggleButton value="list" aria-label="table data">
                  <ListAltRoundedIcon fontSize="inherit" />
                </ToggleButton>
              </ToggleButtonGroup>
            </div>
            {monthlyStatisticOptions.chartType === "bar" && (
              <DashboardBarChart
                chartData={monthlyStatistics}
                loading={loadingData.monthlyLeaveCounts || !monthlyStatistics}
                chartTitle="Leave Statistics"
                isStackedChart={monthlyStatisticOptions.isStacked}
                isHorizontalBar={monthlyStatisticOptions.isHorizontal}
                ignoreBorder
              />
            )}

            {monthlyStatisticOptions.chartType === "line" && (
              <DashboardLineChart
                chartData={monthlyStatistics}
                loading={loadingData.monthlyLeaveCounts || !monthlyStatistics}
                chartTitle="Leave Statistics"
                ignoreBorder
              />
            )}

            {monthlyStatisticOptions.chartType === "list" && (
              <TableContainer
                component={Paper}
                sx={{ maxHeight: 440, overflow: "hidden" }}
              >
                <Table
                  size="small"
                  aria-label="leave statistics monthly counts"
                  stickyHeader
                >
                  <TableHead>
                    <TableRow>
                      <TableCell>Leaves</TableCell>
                      {(monthlyStatistics?.labels || []).map((label, index) => (
                        <TableCell align="right" key={index + label}>
                          {label}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(monthlyStatistics?.datasets || []).map((leave, index) => (
                      <TableRow
                        key={index + leave.label}
                        sx={{
                          "&:last-child td, &:last-child th": { border: 0 },
                        }}
                      >
                        <TableCell component="th" scope="row">
                          {leave.label}
                        </TableCell>
                        {leave.data.map((totalLeave, itemIndex) => (
                          <TableCell
                            align="right"
                            key={index + itemIndex + totalLeave}
                          >
                            {totalLeave}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </div>

          <div className="row my-4">
            <div className="col-12">
              <div className="elevated-0 border rounded my-md-0 my-2 p-2">
                <EventCalendar
                  events={calendarEvents}
                  // calendarDateRange={calendarDateRange}
                  setCalendarDateRange={setCalendarDateRange}
                  loading={loadingData.calendarEvents}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="col-12 col-md-3 my-md-0 my-2 py-2">
          <div className="elevated-0 border rounded w-100 p-2 mb-3">
            <span className="text-uppercase text-primary font-weight-bold">
              Weekly Leave Summary
            </span>

            <div
              className="mt-3"
              style={{
                minHeight:
                  loadingData.weeklyLeaveSummary ||
                  weeklyLeaveSummary.length <= 6
                    ? "50px"
                    : "400px",
                maxHeight: "400px",
                overflowY: "auto",
              }}
            >
              {loadingData.weeklyLeaveSummary ? (
                <div>
                  <Skeleton
                    variant="rect"
                    height={50}
                    className="mb-1 rounded"
                  />
                  <Skeleton
                    variant="rect"
                    height={50}
                    className="mb-1 rounded"
                  />
                  <Skeleton
                    variant="rect"
                    height={50}
                    className="mb-1 rounded"
                  />{" "}
                  <Skeleton
                    variant="rect"
                    height={50}
                    className="mb-1 rounded"
                  />
                  <Skeleton
                    variant="rect"
                    height={50}
                    className="mb-1 rounded"
                  />
                </div>
              ) : (
                weeklyLeaveSummary.map((item) => (
                  <div
                    key={item.id}
                    className="w-100 d-flex align-items-center justify-content-between mb-2 p-2 rounded border"
                  >
                    <div className="d-flex align-items-center">
                      <Avatar
                        alt={item.employeeNames}
                        src={item.profileImage}
                      />
                      <div className="d-flex flex-column ml-1">
                        <span className="font-weight-bold">
                          {item.employeeNames}
                        </span>
                        <span
                          className="text-muted"
                          style={{
                            fontSize: "12px",
                          }}
                        >
                          {item.positionName}
                        </span>
                      </div>
                    </div>

                    <span
                      className="badge badge-pill badge-light"
                      style={{
                        fontSize: "12px",
                      }}
                    >
                      {!!item.isOneDayLeave
                        ? moment(item.start).format("dddd")
                        : `${moment(item.start).format("MMM DD")} - ${moment(
                            item.end
                          ).format("MMM DD")}`}
                    </span>
                  </div>
                ))
              )}

              {!loadingData.weeklyLeaveSummary &&
                weeklyLeaveSummary.length === 0 && (
                  <div className="text-center my-2">No data found</div>
                )}
            </div>
          </div>

          {roleType === "HOU" && (
            <div className="elevated-0 border rounded p-2 w-100 mb-3">
              <span className="text-uppercase text-primary font-weight-bold">
                Other Leave Summary
              </span>

              <div
                className="mt-3"
                style={{
                  maxHeight: "300px",
                  overflowY: "auto",
                }}
              >
                {loadingData.otherLeaveSummary ? (
                  <div>
                    <Skeleton
                      variant="rect"
                      height={50}
                      className="mb-1 rounded"
                    />
                    <Skeleton
                      variant="rect"
                      height={50}
                      className="mb-1 rounded"
                    />
                    <Skeleton
                      variant="rect"
                      height={50}
                      className="mb-1 rounded"
                    />
                    <Skeleton
                      variant="rect"
                      height={50}
                      className="mb-1 rounded"
                    />
                    <Skeleton
                      variant="rect"
                      height={50}
                      className="mb-1 rounded"
                    />
                  </div>
                ) : (
                  otherLeaveSummary.map((item) => (
                    <div
                      key={item.id}
                      className="d-flex align-items-center mb-2 p-2 rounded border"
                    >
                      <div className="d-flex flex-column flex-sm-row align-items-start align-items-sm-center justify-content-between">
                        <h6
                          style={{
                            fontSize: "14px",
                            fontWeight: "bold",
                          }}
                          className="ml-1 mb-1"
                        >
                          {item.name}
                        </h6>
                        <div>
                          <span
                            className="badge badge-light text-left text-uppercase"
                            style={{ minWidth: "90px" }}
                          >
                            <span
                              className="badge badge-light bg-secondary text-light"
                              style={{ fontSize: "11px" }}
                            >
                              {item.pending}
                            </span>{" "}
                            Pending
                          </span>
                          <span
                            className="ml-1 badge badge-info text-left text-uppercase"
                            style={{ minWidth: "90px" }}
                          >
                            <span
                              className="badge badge-light bg-light text-primary"
                              style={{ fontSize: "11px" }}
                            >
                              {item.approved}
                            </span>{" "}
                            Approved
                          </span>
                          <span
                            className="ml-1 badge badge-success text-left text-uppercase"
                            style={{ minWidth: "90px" }}
                          >
                            <span
                              className="badge badge-light bg-light text-secondary"
                              style={{ fontSize: "11px" }}
                            >
                              {item.onLeave}
                            </span>{" "}
                            OnLeave
                          </span>
                        </div>
                      </div>
                    </div>
                  ))
                )}
                {!loadingData.otherLeaveSummary &&
                  otherLeaveSummary.length === 0 && (
                    <div className="text-center my-2">No data found</div>
                  )}
              </div>
            </div>
          )}

          <div className="elevated-0 border rounded p-2 w-100 mb-3">
            <span className="text-uppercase text-primary font-weight-bold">
              Public Holidays
            </span>

            <div
              className="mt-3"
              style={{
                maxHeight: "300px",
                overflowY: "auto",
              }}
            >
              {loadingData.publicHolidays ? (
                <div>
                  <Skeleton
                    variant="rect"
                    height={50}
                    className="mb-1 rounded"
                  />
                  <Skeleton
                    variant="rect"
                    height={50}
                    className="mb-1 rounded"
                  />
                  <Skeleton
                    variant="rect"
                    height={50}
                    className="mb-1 rounded"
                  />
                  <Skeleton
                    variant="rect"
                    height={50}
                    className="mb-1 rounded"
                  />
                  <Skeleton
                    variant="rect"
                    height={50}
                    className="mb-1 rounded"
                  />
                </div>
              ) : (
                publicHolidays.map((holiday) => (
                  <div
                    key={holiday.id}
                    className="d-flex align-items-center mb-3"
                  >
                    <div
                      className="d-flex flex-column justify-content-center date-card text-center"
                      style={{ width: "50px", height: "50px" }}
                    >
                      <span className="p-0">{holiday.dayOfMonth}</span>
                      <span className="p-0">{holiday.abbreviatedMonth}</span>
                    </div>
                    <div className="ml-2">
                      <h6
                        style={{
                          fontSize: "14px",
                          fontWeight: "bold",
                        }}
                        className="m-0"
                      >
                        {holiday.name}
                      </h6>
                      <p
                        className="m-0"
                        style={{
                          fontSize: "12px",
                          color: "#929292",
                          fontWeight: "bold",
                        }}
                      >
                        {holiday.fullDayOfWeek}
                      </p>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

const mapStateToProps = ({
  publicHolidays,
  selectedFiscalYear,
  units,
  user,
  employeeGroups,
}) => {
  return {
    publicHolidays,
    selectedFiscalYear,
    units,
    user,
    employeeGroups,
  };
};
export default connect(mapStateToProps, {
  getLeavePlanProgressTracker,
  getLeaveRequestStatus,
  getTreeUnits,
  getEmployeeGroups,
  getLeaveMonthlyLeaveCounts,
  getEmployeeLeaves,
  downloadEmployeeLeaveReport,
  getOtherLeaveSummary,
  getPublicHolidays,
})(OversightDashboard);
